import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import {
  PLAY_STORE_URL,
  APP_STORE_URL,
} from "../../utils/seo/seoConfig";

// Shown once per SESSION, on phones only: a nudge to install the native app,
// linking to the right store for the device. Dismissing it (or tapping through)
// silences it until the tab is closed and reopened — sessionStorage, not
// localStorage, so it isn't a one-and-done that a user can never see again, but
// it also can't nag on every route change.
const SEEN_KEY = "jg-install-prompt-seen";
// Maps gets its own key so the nudge still appears the first time someone opens
// Maps in a session, even if they already dismissed it on another page. Maps is
// where the native app is most obviously better (GPS, offline tiles), so it's
// worth one showing there rather than being silenced by an earlier dismissal.
const SEEN_KEY_MAPS = "jg-install-prompt-seen-maps";

// iPadOS 13+ reports itself as a Mac, so the touch check is what separates a
// real Mac from an iPad. Anything not iOS gets the Play Store, which is also the
// sane fallback for an unknown mobile UA.
function storeUrlFor(ua, hasTouch) {
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Mac/.test(ua) && hasTouch);
  return isIOS ? APP_STORE_URL : PLAY_STORE_URL;
}

export default function InstallAppPrompt() {
  const [show, setShow] = useState(false);
  const [href, setHref] = useState(PLAY_STORE_URL);
  const { pathname } = useLocation();
  const onMaps = pathname.startsWith("/maps");
  const seenKey = onMaps ? SEEN_KEY_MAPS : SEEN_KEY;

  useEffect(() => {
    setShow(false); // re-evaluate on every route change

    if (typeof window === "undefined") return undefined;

    // Already dismissed this session (for this surface).
    let seen = false;
    try {
      seen = sessionStorage.getItem(seenKey) === "1";
    } catch {
      /* private mode / storage blocked — just show it */
    }
    if (seen) return undefined;

    // Phones only, and never inside an installed PWA (they already "have" it).
    const isPhone = window.matchMedia("(max-width: 1023px)").matches;
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (!isPhone || standalone) return undefined;

    const ua = window.navigator.userAgent || "";
    setHref(storeUrlFor(ua, "ontouchend" in document));

    // Let the page paint first; appearing mid-load feels like an ad.
    const t = setTimeout(() => setShow(true), onMaps ? 600 : 1200);
    return () => clearTimeout(t);
  }, [seenKey, onMaps]);

  const dismiss = () => {
    setShow(false);
    try {
      sessionStorage.setItem(seenKey, "1");
    } catch {
      /* ignore */
    }
  };

  if (!show) return null;

  return (
    <>
      <style>{`.jh-support-fab{display:none !important}`}</style>
      <div
      role="dialog"
      aria-label="Install the JaaGa app"
      data-hide-on-keyboard
      // Parked above the fixed bottom nav (h-16) plus the iOS home-indicator
      // inset, so it never covers the tab bar. z-940 clears the nav (z-900) but
      // stays under the login popup / modals (z-1100+), which must win.
      className="fixed inset-x-0 z-[940] px-3 lg:hidden"
      style={{
        bottom: "calc(4rem + env(safe-area-inset-bottom, 0px) + 8px)",
      }}
    >
      <div className="relative flex items-center gap-3 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#0c2244] via-[#123056] to-[#1a3f78] p-3 pr-10 shadow-[0_20px_50px_-18px_rgba(12,34,68,0.75)]">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[#1c4b93]/20 blur-3xl"
        />

        {/* The real app icon (public/icon-192.png) — the same mark the store
            listing and the home-screen shortcut use. */}
        <img
          src="/icon-192.png"
          alt=""
          aria-hidden="true"
          width="44"
          height="44"
          className="relative h-11 w-11 shrink-0 rounded-xl object-cover shadow-md ring-1 ring-white/20"
        />

        <div className="relative min-w-0 flex-1">
          <p className="text-[13px] font-bold leading-tight text-white">
            For the best experience, use our mobile app
          </p>
          <p className="mt-0.5 truncate text-[11.5px] text-white/65">
            {onMaps
              ? "Smoother maps, survey search & your locker"
              : "Faster access to your documents & property"}
          </p>
        </div>

        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={dismiss}
          className="relative inline-flex shrink-0 items-center gap-1 rounded-xl bg-gradient-to-r from-[#ffc629] to-[#f0a500] px-3.5 py-2 text-[12.5px] font-extrabold text-[#14213d] shadow-[0_10px_22px_-10px_rgba(240,165,0,0.9)]"
        >
          Install
          <ArrowRight size={14} />
        </a>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X size={14} />
        </button>
      </div>
      </div>
    </>
  );
}
